import {
  FaCheck,
  FaFontAwesomeFlag,
  FaLock,
  FaLockOpen,
  FaPen,
  FaSignature,
  FaTrashAlt,
} from '@meronex/icons/fa';
import { GoCloudDownload, GoCloudUpload } from '@meronex/icons/go';
import { useContext } from 'react';
import { Box, Flex, Image, Text } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import store from '../../../../../store';
import { uploaderActions } from '../../../../../store/fileUploadSlice';
import { isChild } from '../../../../../utils/dom';
import { IconButton } from '../../../../common';
import UploaderContext from '../../../../common/uploader/context';
import DocumentInfoModal from '../../../common/modals/DocumentInfoModal';
import { FileElementContext } from './FileElement';
import InfoButton from './InfoButton';

const IconsUploaderListElement = () => {
  const {
    file,
    fontColor,
    forGrid: isForGrid,
    isDisabled,
    isReadonly,
    noDownload,
    noErase,
    noLock,
    noModify,
    noSign,
    noUpload,
    noVerifySign,
    onCheckSignature,
    onDownloadFile,
    onEditFile,
    onLockFile,
    onRemoveFile,
    onTranslateFile,
    onSignFile,
    onVersion,
  } = useContext(FileElementContext);
  const { api } = useContext(UploaderContext);

  const isSelected = useAppSelector((global) =>
    global.fileUploadSlice.uploaders[api.id].selectedFiles.includes(file.docId),
  );

  return (
    <Box
      className={`uploader-list__content ${isSelected ? 'selected' : ''}`}
      onClick={(ev) => {
        if (
          isChild(ev.target as HTMLElement, (current) =>
            current.classList.contains('fileUploaderMenu'),
          )
        )
          return;
        if (ev.ctrlKey) {
          store.dispatch(
            uploaderActions.toggleFileSelection({
              fileId: file.docId,
              id: api.id,
            }),
          );
        } else {
          store.dispatch(
            uploaderActions.selectFile({
              fileId: file.docId,
              id: api.id,
            }),
          );
        }
      }}
    >
      {file?.preview && (
        <Image className="uploader-list__img" src={file?.preview} />
      )}
      <Flex
        className="uploader-list__buttons"
        sx={{
          display: isForGrid ? 'contents' : 'flex',
          flex: isForGrid ? null : 4,
          gap: isForGrid ? null : 2,
          flexWrap: 'wrap',
        }}
      >
        {!noErase && !isReadonly && !isDisabled && file.canEdit && (
          <IconButton
            className="uploader-list__buttons__del"
            title={window.BTN_FILE_ERASE_LBL}
            onClick={() => onRemoveFile(file)}
            icon={FaTrashAlt}
            size={32}
            iconSize={18}
          />
        )}
        {parseInt(file?.docId, 10) > 0 &&
          !isReadonly &&
          !isDisabled &&
          file.canEdit &&
          !noModify && (
            <IconButton
              className="uploader-list__buttons__modify"
              title={window.LBL_EDIT}
              onClick={() => onEditFile(file)}
              icon={FaPen}
              size={32}
              iconSize={18}
            />
          )}

        <DocumentInfoModal file={file}>
          {file.canRead && <InfoButton file={file} />}
        </DocumentInfoModal>
        {file.canEdit && Number(file.docId) >= 0 && !noUpload && (
          <IconButton
            icon={GoCloudUpload}
            className="uploader-list__buttons__up"
            title={window.BTN_FILE_UPLOAD_LBL}
            onClick={() => {
              if (onVersion) onVersion(file);
            }}
            size={32}
            iconSize={20}
          />
        )}
        {!noDownload && (file.canEdit || file.canRead) && (
          <IconButton
            className="uploader-list__buttons__down"
            title={window.BTN_FILE_DOWNLOAD_LBL}
            onClick={() => {
              onDownloadFile(file);
            }}
            icon={GoCloudDownload}
            size={32}
            iconSize={20}
            disabled={!file.canRead && !file.canEdit}
          />
        )}
        {!noLock &&
          !isReadonly &&
          !isDisabled &&
          file.canEdit &&
          parseInt(file?.docId, 10) > 0 && (
            <IconButton
              className={`uploader-list__buttons__lock ${
                file.isLocked && !file.isLockedByMe ? 'lockedByOther' : ''
              }`}
              onClick={() => onLockFile(file)}
              title={file.lockingUser || window.BTN_FILE_LOCK_LBL}
              icon={!file.isLocked ? FaLockOpen : FaLock}
              size={32}
              iconSize={20}
              disabled={String(file?.canEdit) === 'false'}
              isToggled={file?.isLocked}
            />
          )}

        {!noSign && !isReadonly && !isDisabled && file.canEdit && (
          <IconButton
            className="uploader-list__buttons__sign"
            onClick={() => {
              if (onSignFile) onSignFile(file);
            }}
            icon={FaSignature}
            size={32}
            iconSize={20}
            title={window.BTN_MARK_FRM_TO_SIGN}
            isToggled={file.isMarkedToSign}
          />
        )}

        {!noVerifySign &&
          (file.canRead || file.canEdit) &&
          Number(file?.docId) > 0 && (
            <IconButton
              className="uploader-list__buttons__checkSign"
              onClick={() => {
                if (onCheckSignature) onCheckSignature(file);
              }}
              icon={FaCheck}
              size={32}
              iconSize={20}
              title={window.BTN_VERIFY_FRM_SIGN}
              disabled={!file.canRead && !file.canEdit}
            />
          )}

        {onTranslateFile && (
          <IconButton
            className="uploader-list__buttons__translate"
            onClick={() => {
              if (onTranslateFile) onTranslateFile(file);
            }}
            icon={FaFontAwesomeFlag}
            size={32}
            iconSize={20}
            title={window.BTN_TRANSLATE}
            disabled={!file.canRead && !file.canEdit}
          />
        )}
      </Flex>
      <Flex>
        <Text
          className="uploader-list__filename"
          title={file?.name || file?.docName}
          sx={{
            flex: isForGrid ? 3 : 8,
            color: fontColor || null,
            ml: isForGrid ? 2 : null,
            display: isForGrid ? '-webkit-box' : 'block',
            WebkitLineClamp: isForGrid ? '2' : null,
            WebkitBoxOrient: isForGrid ? 'vertical' : null,
            overflow: isForGrid ? 'hidden' : null,
            textOverflow: isForGrid ? 'ellipsis' : null,
            height: isForGrid ? '38px' : null,
          }}
        >
          {file?.name || file?.docName}
        </Text>
      </Flex>
    </Box>
  );
};

export default IconsUploaderListElement;
